import { useEffect } from "react";
import { posthog } from "posthog-js";
import request from "@/lib/api/handler";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { PrescreenLayout } from "@/components/prescreen";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import CandidateInformationInfoCardContent from "./InformationInfoCardContent";

import { useSetUserPrescreenIntent } from "@/mutations/user/set-user-prescreen-intent";
import { useDraftProfileStore } from "@/stores/draft-profile";

const formSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email().optional(),
  phoneNumber: z.string().transform((val, ctx) => {
    const number = val.replace(/\D/g, "");

    if (number.length !== 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_string,
        message: "Phone number must be 10 digits",
        validation: "regex",
      });

      return z.NEVER;
    }

    return number;
  }),
  marketingConsent: z.boolean().default(false),
});

type FormSchema = z.infer<typeof formSchema>;

const CandidatePrescreenTwo = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  const { setUserPrescreenIntent } = useSetUserPrescreenIntent();
  const {
    setDraftProfileSessionId,
    setDraftProfileEmail,
    setDraftProfileIntent,
  } = useDraftProfileStore();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
  });

  const { isPending, mutate, error } = useMutation<string, Error, FormSchema>({
    mutationKey: ["pre-screen", "candidate", "your-information"],
    mutationFn: async (variables) => {
      return await request({
        method: "POST",
        path: "/api/user/draft-profile",
        body: JSON.stringify({
          ...variables,
          name: `${variables.firstName} ${variables.lastName}`,
          intendedRole: "Candidate",
        }),
      });
    },
    onSuccess: (result, { email }) => {
      setDraftProfileSessionId(result);
      setDraftProfileEmail(email ?? null);
      setDraftProfileIntent("candidate");
      posthog.capture("candidate_basic_info");

      return navigate("/pre-screen/candidate/terms-of-service");
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setUserPrescreenIntent("Candidate"), []);

  const handleLogin = () =>
    void (async () => {
      await loginWithRedirect({
        appState: {
          returnTo: "/landing",
        },
      });
    })();

  const { marketingConsent } = form.watch();

  return (
    <PrescreenLayout
      title={"Apply to Join the Community"}
      subtitle={
        "Find high-impact projects or work that fits your life. Only the top 1% are accepted."
      }
      form={
        <Form {...form}>
          <div className="flex-1 flex flex-col gap-4 justify-between self-stretch">
            <div className="flex flex-col gap-2">
              {error && (
                <>
                  <div className="w-full flex flex-row items-center gap-4 bg-card border rounded-md p-8">
                    <AlertTriangle />
                    {error.message}
                  </div>
                </>
              )}
              <form className="pt-4 pb-5 w-full grid grid-cols-2 gap-5">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {!isAuthenticated && (
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>Email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="john.smith@outlook.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Phone Number</FormLabel>
                      <FormControl>
                        <Input placeholder="123-456-7890" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
              <div
                className="w-fit text-sm text-[#2E2E38] underline cursor-pointer"
                onClick={() => navigate("/pre-screen/company/two")}
              >
                I&apos;m a company looking for talent
              </div>
              {!isAuthenticated && (
                <div
                  className="w-fit  text-sm text-[#2E2E38] underline cursor-pointer"
                  onClick={handleLogin}
                >
                  Already have an account? Sign in.
                </div>
              )}
            </div>
            <div className="flex flex-col gap-4">
              <div className="w-full flex flex-row gap-4">
                <Checkbox
                  checked={marketingConsent}
                  onCheckedChange={(state) => {
                    form.setValue(
                      "marketingConsent",
                      typeof state === "string" ? false : state,
                    );
                  }}
                />
                <p className="text-xs text-[#89899F] font-medium">
                  I agree to receive automated text messages and emails from
                  Venturous for marketing updates. Consent is a not a condition
                  of purchase. Reply HELP for help and STOP to unsubscribe. Msg
                  frequency varies. Msg and data rates may apply. Subject to{" "}
                  <a
                    href="https://venturous.work/terms-and-conditions"
                    className="text-inline-link hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://venturous.work/privacy-policy"
                    className="text-inline-link hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Notice
                  </a>
                  .
                </p>
              </div>
              <div className="w-full h-[1px] bg-border" />
              <div className="w-full flex flex-row justify-between">
                <Button
                  variant="stroke"
                  onClick={() => navigate("/pre-screen/")}
                >
                  Back
                </Button>
                <Button
                  variant="success"
                  disabled={!form.formState.isValid || isPending ? true : false}
                  onClick={() =>
                    void form.handleSubmit((data) => mutate(data))()
                  }
                >
                  Continue
                  {isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <ArrowRight />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Form>
      }
      infoCardContent={<CandidateInformationInfoCardContent />}
    />
  );
};

export default CandidatePrescreenTwo;
